const toInt = (value: number) => Math.trunc(value)

export type JalaliDate = {
  jy: number
  jm: number
  jd: number
}

export const JALALI_MONTHS = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند'
] as const

export const JALALI_WEEKDAYS = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'] as const

export const toPersianDigits = (value: string | number) =>
  String(value).replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)])

export function gregorianToJalali(
  gy: number,
  gm: number,
  gd: number
): JalaliDate {
  const g_d_m = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334]
  const gy2 = gm > 2 ? gy + 1 : gy
  let days =
    355666 +
    365 * gy +
    toInt((gy2 + 3) / 4) -
    toInt((gy2 + 99) / 100) +
    toInt((gy2 + 399) / 400) +
    gd +
    g_d_m[gm - 1]
  let jy = -1595 + 33 * toInt(days / 12053)
  days %= 12053
  jy += 4 * toInt(days / 1461)
  days %= 1461
  if (days > 365) {
    jy += toInt((days - 1) / 365)
    days = (days - 1) % 365
  }
  const jm = days < 186 ? 1 + toInt(days / 31) : 7 + toInt((days - 186) / 30)
  const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30)
  return { jy, jm, jd }
}

export function jalaliToGregorian(jy: number, jm: number, jd: number) {
  const year = jy + 1595
  let days =
    -355668 +
    365 * year +
    toInt(year / 33) * 8 +
    toInt(((year % 33) + 3) / 4) +
    jd +
    (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186)

  let gy = 400 * toInt(days / 146097)
  days %= 146097
  if (days > 36524) {
    days -= 1
    gy += 100 * toInt(days / 36524)
    days %= 36524
    if (days >= 365) days += 1
  }
  gy += 4 * toInt(days / 1461)
  days %= 1461
  if (days > 365) {
    gy += toInt((days - 1) / 365)
    days = (days - 1) % 365
  }

  let gd = days + 1
  const leap = (gy % 4 === 0 && gy % 100 !== 0) || gy % 400 === 0
  const monthDays = [0, 31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  let gm = 1
  while (gm <= 12 && gd > monthDays[gm]) {
    gd -= monthDays[gm]
    gm += 1
  }
  return { gy, gm, gd }
}

export function jalaliMonthLength(jy: number, jm: number) {
  if (jm <= 6) return 31
  if (jm <= 11) return 30
  const gregorian = jalaliToGregorian(jy, 12, 30)
  const jalali = gregorianToJalali(gregorian.gy, gregorian.gm, gregorian.gd)
  return jalali.jy === jy && jalali.jm === 12 && jalali.jd === 30 ? 30 : 29
}

export function dateToJalali(date: Date): JalaliDate {
  return gregorianToJalali(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

export function jalaliToDate(jy: number, jm: number, jd: number) {
  const { gy, gm, gd } = jalaliToGregorian(jy, jm, jd)
  return new Date(gy, gm - 1, gd)
}

export function formatGregorianIso(date: Date) {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function parseGregorianIso(value?: string | null) {
  if (!value?.trim()) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
  if (!match) {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return Number.isNaN(date.getTime()) ? null : date
}

export function formatJalaliDisplay(date: Date) {
  const { jy, jm, jd } = dateToJalali(date)
  return toPersianDigits(
    `${jy}/${`${jm}`.padStart(2, '0')}/${`${jd}`.padStart(2, '0')}`
  )
}

export function shiftJalaliMonth(jy: number, jm: number, amount: number) {
  const index = jy * 12 + (jm - 1) + amount
  return {
    jy: Math.floor(index / 12),
    jm: (index % 12) + 1
  }
}

export function getMonthGrid(jy: number, jm: number) {
  const firstWeekday = jalaliToDate(jy, jm, 1).getDay()
  const offset = (firstWeekday + 1) % 7
  const daysInMonth = jalaliMonthLength(jy, jm)

  return Array.from({ length: 42 }, (_, index) => {
    const day = index - offset + 1
    if (day < 1 || day > daysInMonth) return null
    return day
  })
}
